import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import { PaymentAddonProduct } from "src/utils/paymentAddon";
import { userAcceptedSuggestedAddress } from "./checkoutPhysicalAddress/action-creators";

/**
 * Payment type constants
 */
export enum paymentTypes {
  CREDIT_CARD = "CREDIT_CARD",
  PAYPAL = "PAYPAL",
}

export interface BillingAddress {
  firstName?: string;
  lastName?: string;
  name?: string;
  country: string;
  city: string;
  region: string;
  address1: string;
  address2?: string;
  zipcode: string;
}

export enum accordionStates {
  IN_PROGRESS = "IN_PROGRESS",
  LOCKED = "LOCKED",
  COMPLETE = "COMPLETE",
  ERROR = "ERROR",
}

export type AccordionType =
  | "personalizedCourse"
  | "paymentInfo"
  | "accountInfo"
  | "reviewTermsAndSubmit";

export type AccordionInfo = {
  name: AccordionType;
  opened: boolean;
  nextAccordion?: AccordionType;
  showContinueButton?: boolean;
  step: number;
  state: accordionStates;
};

export const accordionsInitialState: Record<AccordionType, AccordionInfo> = {
  personalizedCourse: {
    name: "personalizedCourse",
    opened: true,
    nextAccordion: "paymentInfo",
    showContinueButton: true,
    state: accordionStates.IN_PROGRESS,
    step: 1,
  },
  paymentInfo: {
    name: "paymentInfo",
    opened: false,
    nextAccordion: "accountInfo",
    showContinueButton: true,
    state: accordionStates.LOCKED,
    step: 2,
  },
  accountInfo: {
    name: "accountInfo",
    opened: false,
    nextAccordion: "reviewTermsAndSubmit",
    showContinueButton: true,
    state: accordionStates.LOCKED,
    step: 3,
  },
  reviewTermsAndSubmit: {
    name: "reviewTermsAndSubmit",
    opened: false,
    showContinueButton: false,
    state: accordionStates.LOCKED,
    step: 4,
  },
};

/** Telex289 order: Personalized Plan → Account → Payment → Review */
export const accordionsInitialStateTelex289: Record<
  AccordionType,
  AccordionInfo
> = {
  personalizedCourse: {
    name: "personalizedCourse",
    opened: true,
    nextAccordion: "accountInfo",
    showContinueButton: true,
    state: accordionStates.IN_PROGRESS,
    step: 1,
  },
  accountInfo: {
    name: "accountInfo",
    opened: false,
    nextAccordion: "paymentInfo",
    showContinueButton: true,
    state: accordionStates.LOCKED,
    step: 2,
  },
  paymentInfo: {
    name: "paymentInfo",
    opened: false,
    nextAccordion: "reviewTermsAndSubmit",
    showContinueButton: true,
    state: accordionStates.LOCKED,
    step: 3,
  },
  reviewTermsAndSubmit: {
    name: "reviewTermsAndSubmit",
    opened: false,
    showContinueButton: false,
    state: accordionStates.LOCKED,
    step: 4,
  },
};

export interface CheckoutState {
  salesTaxAmount: number;
  salesTaxState: string;
  salesTaxRate: number;
  trialFeeSalesTaxAmount: number;
  trialFeeSalesTaxRate: number;
  paypalAccount: string;
  paypalError?: string;
  paymentType?: paymentTypes;
  purchaseErrorCode?: errorConstants | Error | "";
  processing: {
    isProcessing: boolean;
    message?: string;
  };
  showCourseEnhancementEdit: boolean;
  goalMonth: string;
  // TODO(patrick): Move these modal states out of redux and into the component states if possible.
  madeTrialPriceChoice: boolean;
  isTimerExpired: boolean;
  isTaxInclusive: boolean;
  isTrialFeeTaxInclusive: boolean;
  walletPay: boolean;
  applePay: boolean;
  googlePay: boolean;

  billingAddress?: BillingAddress;
  billingAddressErrors?: {
    FIRST_NAME: boolean;
    LAST_NAME: boolean;
    COUNTRY: boolean;
    CITY: boolean;
    REGION: boolean;
    ADDRESS1: boolean;
    ZIPCODE: boolean;
  };

  showInvalidAgeModal?: boolean;
  showEnrollmentErrors?: boolean;
  showPaymentInfoErrors?: boolean;
  showReviewErrors?: boolean;

  stripePaymentElementError?: boolean;

  userStartedEnteringPaymentInfo: boolean;
  paymentInformationSubmitted: boolean;

  multiUserPlan: {
    toggled: boolean;
    secondaryUserSameAddressConfirmation?: boolean;
  };

  paymentAddonsInCart: PaymentAddonProduct[];

  accordions: Record<AccordionType, AccordionInfo>;
  currentAccordion: AccordionType;

  acceptedClinicalOffer: boolean;

  billingReminderOptInChecked: boolean;
}

export const initialState: CheckoutState = {
  salesTaxAmount: 0,
  salesTaxState: "",
  salesTaxRate: 0,
  trialFeeSalesTaxAmount: 0,
  trialFeeSalesTaxRate: 0,
  paypalAccount: "",
  processing: {
    isProcessing: false,
  },
  showCourseEnhancementEdit: false,
  goalMonth: "",
  madeTrialPriceChoice: false,
  isTimerExpired: false,
  isTaxInclusive: false,
  isTrialFeeTaxInclusive: false,
  walletPay: false,
  applePay: false,
  googlePay: false,
  userStartedEnteringPaymentInfo: false,
  paymentInformationSubmitted: false,
  multiUserPlan: {
    toggled: false,
    secondaryUserSameAddressConfirmation: null,
  },
  paymentAddonsInCart: [],
  currentAccordion: "personalizedCourse",
  accordions: accordionsInitialState,
  acceptedClinicalOffer: false,
  billingReminderOptInChecked: false,
};

const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    /* eslint-disable no-param-reassign */
    updateCheckoutState(state, action: PayloadAction<Partial<CheckoutState>>) {
      return { ...state, ...action.payload };
    },
    clearCheckoutProcessingState(state) {
      return {
        ...state,
        processing: {
          isProcessing: false,
          message: undefined,
        },
      };
    },
    setAccordionErrorState(state, action: PayloadAction<AccordionType>) {
      const statePatch: Partial<CheckoutState> = {};
      if (action.payload === "paymentInfo") {
        statePatch.showPaymentInfoErrors = true;
      }
      if (action.payload === "accountInfo") {
        statePatch.showEnrollmentErrors = true;
      }
      if (action.payload === "reviewTermsAndSubmit") {
        statePatch.showReviewErrors = true;
      }

      return {
        ...state,
        ...statePatch,
        accordions: {
          ...state.accordions,
          [action.payload]: {
            ...state.accordions[action.payload],
            state: accordionStates.ERROR,
          },
        },
      };
    },
    addPaymentAddonsToCart(
      state,
      action: PayloadAction<PaymentAddonProduct[]>
    ) {
      action.payload.forEach((addon) => {
        if (!state.paymentAddonsInCart.includes(addon)) {
          state.paymentAddonsInCart.push(addon);
        }
      });
    },
    removePaymentAddonsFromCart(
      state,
      action: PayloadAction<PaymentAddonProduct[]>
    ) {
      state.paymentAddonsInCart = state.paymentAddonsInCart.filter(
        (addon) => !action.payload.includes(addon)
      );
    },
    acceptClinicalOffer(state, action: PayloadAction<boolean>) {
      return { ...state, acceptedClinicalOffer: action.payload };
    },
    resetCheckoutTaxData(state) {
      return {
        ...state,
        salesTaxAmount: 0,
        salesTaxState: "",
        salesTaxRate: 0,
        trialFeeSalesTaxAmount: 0,
        trialFeeSalesTaxRate: 0,
        isTaxInclusive: false,
        isTrialFeeTaxInclusive: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userAcceptedSuggestedAddress, (state, action) => {
      const { isSameAsBillingAddress, suggestedAddress } = action.payload;
      if (isSameAsBillingAddress) {
        Object.assign(state.billingAddress, suggestedAddress);
      }
    });
    /* eslint-enable no-param-reassign */
  },
});

export const {
  updateCheckoutState,
  setAccordionErrorState,
  addPaymentAddonsToCart,
  removePaymentAddonsFromCart,
  clearCheckoutProcessingState,
  acceptClinicalOffer,
  resetCheckoutTaxData,
} = checkoutSlice.actions;

export default checkoutSlice;
